import React from 'react'

import {
  DiningOptionBehavior,
  OrderApprovalStatus,
  OrderStatusData,
  OrderStatusType,
  ThirdPartyDeliveryStatus
} from 'src/apollo/onlineOrdering'
import { CompletedOrder } from 'src/public/components/online_ordering/types'
import { toI18nDateTime, toLocalDateTime } from 'src/shared/js/timeUtils'
import {
  Formats,
  fromUnixTime,
  isAfter
} from '@toasttab/buffet-pui-date-utilities'
import { StageDivider } from 'public/components/default_template/online_ordering/order_tracker/order_status_bar/OrderStatusBar'
import {
  AlertIcon,
  CurbsideIcon,
  DriverIcon,
  HomeIcon,
  OrderConfirmedIcon,
  OrderReadyIcon,
  PickupIcon,
  PreparingIcon,
  RestaurantIcon,
  ScheduledIcon
} from 'public/components/default_template/online_ordering/order_tracker/order_status_bar/OrderStatusIcons'
import { t } from 'src/translations'

export enum UnifiedOrderStatus {
  SCHEDULED = 'SCHEDULED',
  RECEIVED = 'RECEIVED',
  CONFIRMED = 'CONFIRMED',
  PREPARING = 'PREPARING',
  READY_FOR_PICKUP = 'READY_FOR_PICKUP',
  DRIVER_EN_ROUTE_TO_RESTAURANT = 'DRIVER_EN_ROUTE_TO_RESTAURANT',
  DRIVER_EN_ROUTE_TO_DROPOFF = 'DRIVER_EN_ROUTE_TO_DROPOFF',
  DRIVER_AT_DROPOFF = 'DRIVER_AT_DROPOFF',
  DRIVER_AT_RESTAURANT = 'DRIVER_AT_RESTAURANT',
  COMPLETE = 'COMPLETE',
  UNKNOWN = 'UNKNOWN',
  CANCELLED = 'CANCELLED',
  DELIVERY_FAILED = 'DELIVERY_FAILED',
  UNDELIVERABLE = 'UNDELIVERABLE',
  FAILURE_AT_RESTAURANT = 'FAILURE_AT_RESTAURANT',
  LIKELY_READY = 'LIKELY_READY'
}

const orderStatuses = Object.values(OrderStatusType)
const deliveryStatuses: Array<ThirdPartyDeliveryStatus | null> = [
  ...Object.values(ThirdPartyDeliveryStatus),
  null
]

/**
 * Create a map of combinations of OrderStatusType <-> ThirdPartyDeliveryStatus statuses to their UnifiedOrderStatus.
 * ThirdPartyDeliveryStatuses are mapped after OrderStatusTypes since they usually "override" the latter. For example,
 * if the orderStatus is Preparing but the deliveryStatus is DriverEnRouteToDropoff, we can assume the order is much
 * further along in the process than "Preparing"
 *
 */
const combinationMapping = (): MapType => {
  let mapping: MapType = {}

  deliveryStatuses.forEach((deliveryStatus) => {
    mapping[`${OrderStatusType.Received}-${deliveryStatus}`] =
      UnifiedOrderStatus.RECEIVED
    mapping[`${OrderStatusType.Future}-${deliveryStatus}`] =
      UnifiedOrderStatus.SCHEDULED
    mapping[`${OrderStatusType.Preparing}-${deliveryStatus}`] =
      UnifiedOrderStatus.PREPARING
    mapping[`${OrderStatusType.Approved}-${deliveryStatus}`] =
      UnifiedOrderStatus.CONFIRMED
    mapping[`${OrderStatusType.InPreparation}-${deliveryStatus}`] =
      UnifiedOrderStatus.PREPARING
    mapping[`${OrderStatusType.AlmostReady}-${deliveryStatus}`] =
      UnifiedOrderStatus.PREPARING
    mapping[`${OrderStatusType.ReadyForPickup}-${deliveryStatus}`] =
      UnifiedOrderStatus.READY_FOR_PICKUP
    mapping[`${OrderStatusType.AutoClosed}-${deliveryStatus}`] =
      UnifiedOrderStatus.FAILURE_AT_RESTAURANT
    mapping[`${OrderStatusType.Closed}-${deliveryStatus}`] =
      UnifiedOrderStatus.COMPLETE
    mapping[`${OrderStatusType.Unfulfilled}-${deliveryStatus}`] =
      UnifiedOrderStatus.FAILURE_AT_RESTAURANT
    mapping[`${OrderStatusType.Unknown}-${deliveryStatus}`] =
      UnifiedOrderStatus.FAILURE_AT_RESTAURANT
    mapping[`${OrderStatusType.Voided}-${deliveryStatus}`] =
      UnifiedOrderStatus.FAILURE_AT_RESTAURANT
  })

  orderStatuses.forEach((orderStatus) => {
    mapping[`${orderStatus}-${ThirdPartyDeliveryStatus.DriverAtRestaurant}`] =
      UnifiedOrderStatus.DRIVER_AT_RESTAURANT
    mapping[`${orderStatus}-${ThirdPartyDeliveryStatus.Undeliverable}`] =
      UnifiedOrderStatus.UNDELIVERABLE
    mapping[`${orderStatus}-${ThirdPartyDeliveryStatus.DispatchFailure}`] =
      UnifiedOrderStatus.DELIVERY_FAILED
    mapping[`${orderStatus}-${ThirdPartyDeliveryStatus.OrderDelivered}`] =
      UnifiedOrderStatus.COMPLETE
    mapping[
      `${orderStatus}-${ThirdPartyDeliveryStatus.DriverEnRouteToDropoff}`
    ] = UnifiedOrderStatus.DRIVER_EN_ROUTE_TO_DROPOFF
    mapping[`${orderStatus}-${ThirdPartyDeliveryStatus.DriverAtDropoff}`] =
      UnifiedOrderStatus.DRIVER_EN_ROUTE_TO_DROPOFF
    mapping[`${orderStatus}-${ThirdPartyDeliveryStatus.DriverReturnedOrder}`] =
      UnifiedOrderStatus.FAILURE_AT_RESTAURANT
    mapping[`${orderStatus}-${ThirdPartyDeliveryStatus.DeliveryCancelled}`] =
      UnifiedOrderStatus.CANCELLED
    mapping[`${orderStatus}-${ThirdPartyDeliveryStatus.DriverAtDropoff}`] =
      UnifiedOrderStatus.DRIVER_AT_DROPOFF
    mapping[
      `${orderStatus}-${ThirdPartyDeliveryStatus.DeliveryCancelledNotApproved}`
    ] = UnifiedOrderStatus.FAILURE_AT_RESTAURANT
    mapping[
      `${orderStatus}-${ThirdPartyDeliveryStatus.DriverEnRouteToRestaurant}`
    ] = UnifiedOrderStatus.DRIVER_EN_ROUTE_TO_RESTAURANT
    mapping[`${orderStatus}-${ThirdPartyDeliveryStatus.DriverReturnedOrder}`] =
      UnifiedOrderStatus.DELIVERY_FAILED
  })
  return mapping
}

const statusMapping = combinationMapping()

/**
 * Unifies the different statuses into one status enum
 *
 * @param orderStatus
 * @param deliveryStatus
 * @param completedOrder
 */
const getUnifiedOrderStatus = (
  orderStatus: OrderStatusType,
  deliveryStatus: ThirdPartyDeliveryStatus | null,
  completedOrder: CompletedOrder | null
) => {
  const status =
    completedOrder?.approvalStatus === OrderApprovalStatus.Future
      ? OrderStatusType.Future
      : orderStatus
  const key = `${status}-${deliveryStatus}`
  return statusMapping[key] || UnifiedOrderStatus.UNKNOWN
}

export const getUnifiedOrderStatusWithCompletedOrder = (
  orderStatusInfo: OrderStatusData | null | undefined,
  orderStatusInfoLoading: boolean,
  completedOrder: CompletedOrder | null | undefined
) => {
  if (!completedOrder) {
    return UnifiedOrderStatus.UNKNOWN
  }

  const currentTime = new Date(Date.now())
  const estimatedFulfillmentDate = completedOrder?.estimatedFulfillmentDate
    ? fromUnixTime(completedOrder.estimatedFulfillmentDate / 1000)
    : null

  // If TDS order and no tracking details, show default status info
  if (!orderStatusInfo && !orderStatusInfoLoading) {
    return UnifiedOrderStatus.UNKNOWN
  }
  const actualStatus = getUnifiedOrderStatus(
    orderStatusInfo?.guestOrderStatus ?? OrderStatusType.Received,
    orderStatusInfo?.deliveryStatus ?? null,
    completedOrder
  )

  // An order the restaurant has not accepted cannot be ready, however late it is.
  // completedOrder is fetched once per page load while orderStatusInfo polls, so a
  // status past RECEIVED is the fresher signal that the restaurant has acted.
  const awaitingApproval =
    actualStatus === UnifiedOrderStatus.RECEIVED &&
    completedOrder.approvalStatus === OrderApprovalStatus.NeedsApproval

  // Only show "likely ready" for early-stage statuses when est. time has passed
  if (
    estimatedFulfillmentDate &&
    !awaitingApproval &&
    isAfter(Date.now(), estimatedFulfillmentDate) &&
    (actualStatus === UnifiedOrderStatus.RECEIVED ||
      actualStatus === UnifiedOrderStatus.CONFIRMED ||
      actualStatus === UnifiedOrderStatus.PREPARING)
  ) {
    return UnifiedOrderStatus.LIKELY_READY
  }

  if (actualStatus === UnifiedOrderStatus.SCHEDULED) {
    return UnifiedOrderStatus.SCHEDULED
  }

  const twoHoursFromNow = currentTime.getTime() + 2 * 60 * 60 * 1000
  const isScheduled =
    estimatedFulfillmentDate !== null &&
    isAfter(estimatedFulfillmentDate, new Date(twoHoursFromNow))

  if (
    (actualStatus === UnifiedOrderStatus.CONFIRMED ||
      actualStatus === UnifiedOrderStatus.RECEIVED) &&
    isScheduled
  ) {
    return UnifiedOrderStatus.SCHEDULED
  }

  const isTdsOrder = !!completedOrder?.deliveryServiceInfo
  if (isTdsOrder && actualStatus === UnifiedOrderStatus.READY_FOR_PICKUP) {
    return UnifiedOrderStatus.PREPARING
  }

  return actualStatus
}

type MapType = { [key: string]: UnifiedOrderStatus }

export const statusToIcons = (
  orderStatus: UnifiedOrderStatus,
  diningOptionBeahvior: DiningOptionBehavior,
  curbside: boolean
): Array<JSX.Element> => {
  switch (diningOptionBeahvior) {
    case DiningOptionBehavior.Delivery:
      return deliveryStatusToIcons(orderStatus)
    case DiningOptionBehavior.TakeOut:
      return curbside
        ? curbsideStatusToIcons(orderStatus)
        : takeoutStatusToIcons(orderStatus)
    default:
      return []
  }
}

export const statusToCopy = (
  orderStatus: UnifiedOrderStatus,
  diningOptionBehavior: DiningOptionBehavior,
  restaurantName: string,
  pickupInstructions?: string | null,
  driverName?: string,
  completedDate?: Date,
  curbside?: boolean
): StatusBarComponentCopy | null => {
  switch (diningOptionBehavior) {
    case DiningOptionBehavior.Delivery:
      return deliveryStatusToCopy(orderStatus, restaurantName, driverName)
    case DiningOptionBehavior.TakeOut:
      return takeoutStatusToCopy(
        orderStatus,
        restaurantName,
        pickupInstructions,
        completedDate,
        curbside
      )
    default:
      return null
  }
}

const takeoutStatusToIcons = (
  orderStatus: UnifiedOrderStatus
): Array<JSX.Element> => {
  switch (orderStatus) {
    case UnifiedOrderStatus.SCHEDULED:
      // eslint-disable-next-line max-len
      return [
        <ScheduledIcon key='scheduledIcon' />,
        <StageDivider key='divider-1' inProgress />,
        <RestaurantIcon key='restaurantIcon' inProgress />,
        <StageDivider key='divider-2' upcoming />,
        <PickupIcon key='pickupIcon' inProgress />
      ]
    case UnifiedOrderStatus.UNKNOWN:
    case UnifiedOrderStatus.RECEIVED:
      // eslint-disable-next-line max-len
      return [
        <OrderConfirmedIcon key='orderConfirmedIcon' />,
        <StageDivider key='divider-1' inProgress />,
        <RestaurantIcon key='restaurantIcon' inProgress />,
        <StageDivider key='divider-2' upcoming />,
        <PickupIcon key='pickupIcon' inProgress />
      ]
    case UnifiedOrderStatus.CONFIRMED:
      // eslint-disable-next-line max-len
      return [
        <OrderConfirmedIcon key='orderConfirmedIcon' />,
        <StageDivider key='divider-1' inProgress />,
        <RestaurantIcon key='restaurantIcon' inProgress />,
        <StageDivider key='divider-2' upcoming />,
        <PickupIcon key='pickupIcon' inProgress />
      ]
    case UnifiedOrderStatus.PREPARING:
      // eslint-disable-next-line max-len
      return [
        <PreparingIcon key='preparingIcon' />,
        <StageDivider key='divider-1' inProgress />,
        <RestaurantIcon key='restaurantIcon' inProgress />,
        <StageDivider key='divider-2' upcoming />,
        <PickupIcon key='pickupIcon' inProgress />
      ]
    case UnifiedOrderStatus.READY_FOR_PICKUP:
      // eslint-disable-next-line max-len
      return [
        <PreparingIcon key='preparingIcon' />,
        <StageDivider key='divider-1' />,
        <RestaurantIcon key='restaurantIcon' />,
        <StageDivider key='divider-2' inProgress />,
        <PickupIcon key='pickupIcon' inProgress />
      ]
    case UnifiedOrderStatus.CANCELLED:
      return [] // no icons when order is cancelled
    case UnifiedOrderStatus.COMPLETE:
      // eslint-disable-next-line max-len
      return [
        <PreparingIcon key='preparingIcon' />,
        <StageDivider key='divider-1' />,
        <RestaurantIcon key='restaurantIcon' />,
        <StageDivider key='divider-2' />,
        <OrderReadyIcon key='orderReadyIcon' />
      ]
    case UnifiedOrderStatus.FAILURE_AT_RESTAURANT:
      // eslint-disable-next-line max-len
      return [
        <AlertIcon key='alertIcon' />,
        <StageDivider key='divider-1' upcoming />,
        <RestaurantIcon key='restaurantIcon' inProgress />,
        <StageDivider key='divider-2' upcoming />,
        <PickupIcon key='pickupIcon' inProgress />
      ]
    default:
      return []
  }
}

const takeoutStatusToCopy = (
  orderStatus: UnifiedOrderStatus,
  restaurantName: string,
  pickupInstructions?: string | null,
  completedDate?: Date,
  curbside?: boolean
): StatusBarComponentCopy | null => {
  const orderCompletedDate =
    typeof completedDate === 'number' ? new Date(completedDate) : completedDate
  switch (orderStatus) {
    case UnifiedOrderStatus.SCHEDULED:
      return {
        title: t('status-util.order-scheduled'),
        subtitle: t('status-util.your-order-has')
      }
    case UnifiedOrderStatus.UNKNOWN:
    case UnifiedOrderStatus.RECEIVED:
      return {
        title: t('status-util.confirming-order'),
        subtitle: t('status-util.is-confirming', { restaurantName })
      }
    case UnifiedOrderStatus.CONFIRMED:
    case UnifiedOrderStatus.PREPARING:
      return {
        title: t('status-util.preparing-order'),
        subtitle: t('status-util.is-preparing', { restaurantName })
      }
    case UnifiedOrderStatus.READY_FOR_PICKUP:
      return {
        title: t('status-util.ready-for-pickup'),
        subtitle:
          pickupInstructions ??
          `Your order is ready for ${
            curbside ? 'curbside ' : ''
          }pickup at ${restaurantName}`
      }
    case UnifiedOrderStatus.CANCELLED:
      return {
        title: t('status-util.order-cancelled-one'),
        subtitle: t('status-util.order-cancelled-two')
      }
    case UnifiedOrderStatus.COMPLETE:
      return {
        title: t('status-util.order-completed'),
        subtitle: t('status-util.order-was-completed', {
          date: formatOrderCompletedDate(orderCompletedDate)
        })
      }
    case UnifiedOrderStatus.FAILURE_AT_RESTAURANT:
      return {
        title: t('status-util.order-pending'),
        subtitle: t('status-util.order-please-contact', { restaurantName })
      }
    default:
      return null
  }
}

const formatOrderCompletedDate = (date?: Date) => {
  if (date) {
    return ` at ${Intl.DateTimeFormat('en-US', {
      timeStyle: 'short',
      hour12: true,
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone
    }).format(date)}`
  }
  return ''
}

const deliveryStatusToCopy = (
  orderStatus: UnifiedOrderStatus,
  restaurantName: string,
  driverName?: string
): StatusBarComponentCopy | null => {
  switch (orderStatus) {
    case UnifiedOrderStatus.FAILURE_AT_RESTAURANT:
      return {
        title: t('status-util.order-pending'),
        subtitle: t('status-util.order-please-contact', { restaurantName })
      }
    case UnifiedOrderStatus.DELIVERY_FAILED:
      return {
        title: t('status-util.order-failed'),
        subtitle: t('status-util.issue-processing', { restaurantName })
      }
    case UnifiedOrderStatus.SCHEDULED:
      return {
        title: t('status-util.order-scheduled'),
        subtitle: t('status-util.your-order-delivery')
      }
    case UnifiedOrderStatus.UNKNOWN:
    case UnifiedOrderStatus.RECEIVED:
      return {
        title: t('status-util.confirming-order'),
        subtitle: t('status-util.is-confirming', { restaurantName })
      }
    case UnifiedOrderStatus.CONFIRMED:
    case UnifiedOrderStatus.READY_FOR_PICKUP:
    case UnifiedOrderStatus.PREPARING: {
      const driverCopy = driverName
        ? t('status-util.will-be-delivering', { driverName })
        : ''
      // if the driver is assigned, the copy changes to not include the Rx name
      const rxCopy = driverName
        ? t('status-util.order-being-prepared')
        : t('status-util.is-preparing', { restaurantName })
      return {
        title: t('status-util.preparing-order'),
        subtitle: `${rxCopy}.${driverCopy}`
      }
    }
    case UnifiedOrderStatus.DRIVER_EN_ROUTE_TO_RESTAURANT:
      return {
        title: t('status-util.picking-up'),
        subtitle: t('status-util.driver-on-way')
      }
    case UnifiedOrderStatus.DRIVER_AT_RESTAURANT:
      return {
        title: t('status-util.picking-up'),
        subtitle: t('status-util.driver-picking-up', { restaurantName })
      }
    case UnifiedOrderStatus.DRIVER_EN_ROUTE_TO_DROPOFF:
      return {
        title: t('status-util.delivering-order'),
        subtitle: t('status-util.driver-has-order')
      }
    case UnifiedOrderStatus.DRIVER_AT_DROPOFF:
      return {
        title: t('status-util.delivering-order'),
        subtitle: driverName
          ? t('status-util.driver-approaching-one', { driverName })
          : t('status-util.driver-approaching-two')
      }
    case UnifiedOrderStatus.CANCELLED:
      return {
        title: t('status-util.order-cancelled-two'),
        subtitle: t('status-util.please-contact-for', { restaurantName })
      }
    case UnifiedOrderStatus.UNDELIVERABLE:
      return {
        title: t('status-util.unable-to-deliver'),
        subtitle: driverName
          ? t('status-util.your-driver-one', { driverName })
          : t('status-util.your-driver-two')
      }
    case UnifiedOrderStatus.COMPLETE:
      return {
        title: t('status-util.order-completed'),
        subtitle: t('status-util.order-been-completed')
      }
    default:
      return null
  }
}

const curbsideStatusToIcons = (orderStatus: UnifiedOrderStatus) => {
  // curbside matches takeout's flows except for the last icons being different
  let icons = takeoutStatusToIcons(orderStatus)
  icons[icons.length - 1] = (
    <CurbsideIcon inProgress={orderStatus !== UnifiedOrderStatus.COMPLETE} />
  )

  return icons
}

const deliveryStatusToIcons = (
  orderStatus: UnifiedOrderStatus
): Array<JSX.Element> => {
  switch (orderStatus) {
    case UnifiedOrderStatus.SCHEDULED:
      // eslint-disable-next-line max-len
      return [
        <ScheduledIcon key='scheduledIcon' />,
        <StageDivider key='divider-1' inProgress />,
        <RestaurantIcon key='restaurantIcon' inProgress />,
        <StageDivider key='divider-2' upcoming />,
        <DriverIcon key='driverIcon' inProgress />,
        <StageDivider key='divider-3' upcoming />,
        <HomeIcon key='home-icon' inProgress />
      ]
    case UnifiedOrderStatus.UNKNOWN:
    case UnifiedOrderStatus.RECEIVED:
      // eslint-disable-next-line max-len
      return [
        <OrderConfirmedIcon key='orderConfirmedIcon' />,
        <StageDivider key='divider-1' inProgress />,
        <RestaurantIcon key='restaurantIcon' inProgress />,
        <StageDivider key='divider-2' upcoming />,
        <DriverIcon key='driverIcon' inProgress />,
        <StageDivider key='divider-3' upcoming />,
        <HomeIcon key='home-icon' inProgress />
      ]
    case UnifiedOrderStatus.CONFIRMED:
      // eslint-disable-next-line max-len
      return [
        <OrderConfirmedIcon key='orderConfirmedIcon' />,
        <StageDivider key='divider-1' inProgress />,
        <RestaurantIcon key='restaurantIcon' inProgress />,
        <StageDivider key='divider-2' upcoming />,
        <DriverIcon key='driverIcon' inProgress />,
        <StageDivider key='divider-3' upcoming />,
        <HomeIcon key='home-icon' inProgress />
      ]
    case UnifiedOrderStatus.PREPARING:
      // eslint-disable-next-line max-len
      return [
        <PreparingIcon key='preparingIcon' />,
        <StageDivider key='divider-1' inProgress />,
        <RestaurantIcon key='restaurantIcon' inProgress />,
        <StageDivider key='divider-2' upcoming />,
        <DriverIcon key='driverIcon' inProgress />,
        <StageDivider key='divider-3' upcoming />,
        <HomeIcon key='home-icon' inProgress />
      ]
    case UnifiedOrderStatus.READY_FOR_PICKUP:
      // eslint-disable-next-line max-len
      return [
        <PreparingIcon key='preparingIcon' />,
        <StageDivider key='divider-1' />,
        <RestaurantIcon key='restaurantIcon' />,
        <StageDivider key='divider-2' inProgress />,
        <DriverIcon key='driverIcon' inProgress />,
        <StageDivider key='divider-3' upcoming />,
        <HomeIcon key='home-icon' inProgress />
      ]
    case UnifiedOrderStatus.DRIVER_EN_ROUTE_TO_RESTAURANT:
    case UnifiedOrderStatus.DRIVER_AT_RESTAURANT:
      // eslint-disable-next-line max-len
      return [
        <PreparingIcon key='preparingIcon' />,
        <StageDivider key='divider-1' />,
        <RestaurantIcon key='restaurantIcon' />,
        <StageDivider key='divider-2' inProgress />,
        <DriverIcon key='driverIcon' inProgress />,
        <StageDivider key='divider-3' upcoming />,
        <HomeIcon key='home-icon' inProgress />
      ]
    case UnifiedOrderStatus.DRIVER_EN_ROUTE_TO_DROPOFF:
      // eslint-disable-next-line max-len
      return [
        <PreparingIcon key='preparingIcon' />,
        <StageDivider key='divider-1' />,
        <RestaurantIcon key='restaurantIcon' />,
        <StageDivider key='divider-2' />,
        <DriverIcon key='driverIcon' />,
        <StageDivider key='divider-3' inProgress />,
        <HomeIcon key='home-icon' inProgress />
      ]
    case UnifiedOrderStatus.DRIVER_AT_DROPOFF:
      // eslint-disable-next-line max-len
      return [
        <PreparingIcon key='preparingIcon' />,
        <StageDivider key='divider-1' />,
        <RestaurantIcon key='restaurantIcon' />,
        <StageDivider key='divider-2' />,
        <DriverIcon key='driverIcon' />,
        <StageDivider key='divider-3' inProgress />,
        <HomeIcon key='home-icon' inProgress />
      ]
    case UnifiedOrderStatus.COMPLETE:
      // eslint-disable-next-line max-len
      return [
        <PreparingIcon key='preparingIcon' />,
        <StageDivider key='divider-1' />,
        <RestaurantIcon key='restaurantIcon' />,
        <StageDivider key='divider-2' />,
        <DriverIcon key='driverIcon' />,
        <StageDivider key='divider-3' />,
        <HomeIcon key='home-icon' />
      ]
    case UnifiedOrderStatus.CANCELLED:
      return [] // no icons when order is cancelled
    case UnifiedOrderStatus.UNDELIVERABLE:
      // eslint-disable-next-line max-len
      return [
        <PreparingIcon key='preparingIcon' />,
        <StageDivider key='divider-1' />,
        <RestaurantIcon key='restaurantIcon' />,
        <StageDivider key='divider-2' />,
        <AlertIcon key='alertIcon' />,
        <StageDivider key='divider-3' inProgress />,
        <HomeIcon key='home-icon' inProgress />
      ]
    case UnifiedOrderStatus.FAILURE_AT_RESTAURANT:
      // eslint-disable-next-line max-len
      return [
        <AlertIcon key='alertIcon' />,
        <StageDivider key='divider-1' upcoming />,
        <RestaurantIcon key='restaurantIcon' inProgress />,
        <StageDivider key='divider-2' upcoming />,
        <DriverIcon key='driverIcon' inProgress />,
        <StageDivider key='divider-3' upcoming />,
        <HomeIcon key='home-icon' inProgress />
      ]
    default:
      return []
  }
}

export const deliveryStatusToFulfillmentTime = (
  orderStatus: UnifiedOrderStatus,
  orderStatusData: OrderStatusData | undefined,
  order: CompletedOrder,
  locale: string = 'en-US',
  timeZoneId: string | undefined = undefined
): string | null => {
  const estimatedDelivery = orderStatusData?.trackingDetails
    ?.estimatedDeliveryTime
    ? parseInt(orderStatusData.trackingDetails.estimatedDeliveryTime)
    : null
  const fulfillmentTime =
    estimatedDelivery ?? order.estimatedFulfillmentDate ?? null
  const formattedFulfillment = fulfillmentTime
    ? formatFulfillmentTime(fulfillmentTime, locale, timeZoneId)
    : null
  switch (orderStatus) {
    case UnifiedOrderStatus.FAILURE_AT_RESTAURANT:
    case UnifiedOrderStatus.UNKNOWN:
      return t('status-util.estimated-arrival')
    case UnifiedOrderStatus.COMPLETE: {
      const deliveryDateCopy = orderStatusData?.trackingDetails
        ?.actualDeliveryTime
        ? ` ${formatFulfillmentTime(
            parseInt(orderStatusData.trackingDetails.actualDeliveryTime),
            locale,
            timeZoneId
          )}`
        : ''
      return t('status-util.order-was-delivered', { deliveryDateCopy })
    }
    default:
      return formattedFulfillment
        ? t('status-util.estimated-arrival-format', { formattedFulfillment })
        : null
  }
}

export const deliveryStatusToFulfillmentDeadline = (
  orderStatusData: OrderStatusData | undefined,
  locale: string = 'en-US',
  timeZoneId: string | undefined = undefined
): string | null => {
  const dropoffDeadline = orderStatusData?.trackingDetails?.dropoffDeadline
    ? parseInt(orderStatusData.trackingDetails.dropoffDeadline)
    : null
  const formattedDeadline = dropoffDeadline
    ? formatFulfillmentTime(dropoffDeadline, locale, timeZoneId)
    : null
  return formattedDeadline
    ? t('status-util.latest-arrival', { formattedDeadline })
    : null
}

export const takeoutStatusToFulfillmentTime = (
  orderStatus: UnifiedOrderStatus,
  order: CompletedOrder
) => {
  const formattedFulfillment = order.estimatedFulfillmentDate
    ? formatFulfillmentTime(order.estimatedFulfillmentDate)
    : null
  switch (orderStatus) {
    case UnifiedOrderStatus.UNKNOWN:
    case UnifiedOrderStatus.FAILURE_AT_RESTAURANT:
      return t('status-util.est-pickup-time')
    case UnifiedOrderStatus.COMPLETE:
    case UnifiedOrderStatus.READY_FOR_PICKUP:
      return null
    default:
      return formattedFulfillment
        ? t('status-util.order-will-be-ready', { formattedFulfillment })
        : null
  }
}

const formatFulfillmentTime = (
  fulfillmentTime: number,
  locale: string = 'en-US',
  timeZoneId: string | undefined = undefined
) => {
  const date = fromUnixTime(fulfillmentTime / 1000)
  if (locale === 'en-US') {
    return toLocalDateTime(date)
      .replace('Today', 'today')
      .replace('Tomorrow', 'tomorrow')
  } else {
    return toI18nDateTime(date, Formats.dateTime.medium, locale, timeZoneId)
  }
}

type StatusBarComponentCopy = {
  title: string
  subtitle: string
}

export default getUnifiedOrderStatus
